var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import { launch } from "../state";
import { api } from "../api/client";
export function extractLaunchParams(href) {
    const u = new URL(href);
    const actorRaw = u.searchParams.get("actor");
    const endpoint = u.searchParams.get("endpoint");
    const authToken = u.searchParams.get("auth");
    const activityId = u.searchParams.get("activity_id");
    const registration = u.searchParams.get("registration");
    if (!actorRaw || !endpoint || !authToken || !activityId || !registration)
        return null;
    const actor = { name: "", mbox: "" };
    for (const kv of actorRaw.split(",")) {
        const [k, v] = kv.split(":");
        if (k === "name")
            actor.name = v;
        if (k === "mbox")
            actor.mbox = v.startsWith("mailto:") ? v : `mailto:${v}`;
    }
    return { actor, endpoint, authToken, activityId, registration };
}
let LaunchGate = class LaunchGate extends LitElement {
    constructor() {
        super(...arguments);
        this.status = "loading";
        this.errorMsg = "";
    }
    static { this.styles = css `
    :host, *, *::before, *::after { box-sizing: border-box; }
    :host { display: block; padding: 24px; font-family: var(--font-base, sans-serif); }
    .error-screen { display: flex; flex-direction: column; gap: 10px; max-width: 480px; }
    .error-screen h2 { margin: 0; font-size: 18px; font-weight: 600; color: var(--color-slate-800, #1e293b); }
    .error-screen p { margin: 0; font-size: 14px; line-height: 1.5; color: var(--color-slate-600, #475569); }
    .error-screen .detail { font-size: 11px; color: var(--color-slate-400, #94a3b8);
                             font-family: var(--font-mono, monospace); word-break: break-word; }
  `; }
    async connectedCallback() {
        super.connectedCallback();
        const params = extractLaunchParams(location.href);
        if (!params) {
            this.status = "error";
            this.errorMsg = "Missing launch parameters.";
            return;
        }
        try {
            const u = new URL(location.href);
            const resp = await api.verifyLaunch({
                actor: u.searchParams.get("actor") ?? "",
                endpoint: params.endpoint,
                auth: params.authToken,
                activity_id: params.activityId,
                registration: params.registration,
            });
            launch.value = {
                actor: params.actor,
                endpoint: params.endpoint,
                registration: params.registration,
                activityId: params.activityId,
                sessionToken: resp.session_token,
            };
            // Route the SPA based on the scenario kind the server identified — the
            // app's router is hash-based and the launch URL only has query params.
            if (!location.hash || location.hash === "#" || location.hash === "#/") {
                const kindPath = resp.scenario_kind === "pas" ? "pas/activity"
                    : resp.scenario_kind === "batch" ? "batch" : "case";
                location.hash = `#/${kindPath}/${resp.scenario_id}`;
            }
            this.status = "ready";
            this.dispatchEvent(new CustomEvent("launch-ready", { bubbles: true, composed: true }));
        }
        catch (e) {
            this.status = "error";
            this.errorMsg = e.message;
        }
    }
    render() {
        if (this.status === "loading")
            return html `<p>Loading…</p>`;
        if (this.status === "error")
            return html `
      <div class="error-screen" role="alert">
        <h2>We couldn't load this activity</h2>
        <p>Go back to the course page and open the activity again. If this keeps happening, contact student support and quote the code below.</p>
        <p class="detail">Technical detail: ${this.errorMsg}</p>
      </div>`;
        return html `<slot></slot>`;
    }
};
__decorate([
    state()
], LaunchGate.prototype, "status", void 0);
__decorate([
    state()
], LaunchGate.prototype, "errorMsg", void 0);
LaunchGate = __decorate([
    customElement("launch-gate")
], LaunchGate);
export { LaunchGate };
